<template>
    <div class="change-password-page" v-cloak>
        <div class="page-body">
            <section class="card panel">
                <div class="security-banner">
                    <div class="badge">安全等级</div>
                    <div class="banner-texts">
                        <div class="banner-title">找回密码</div>
                        <p class="banner-desc">建议使用 8 位以上密码，并包含字母与数字。</p>
                    </div>
                </div>

                <a-form class="password-form" :model="form" layout="vertical">
                    <a-form-item label="账户">
                        <a-input v-model:value="form.email" placeholder="请输入您的账户,账户为邮箱地址" />
                    </a-form-item>
                    <a-form-item label="验证码">
                        <div class="code-row">
                            <a-input v-model:value="form.code" placeholder="输入验证码" />
                            <button class="code-btn" type="button" :disabled="codeLoading || codeCountdown > 0"
                                @click="handleGetCode">
                                {{ codeLoading ? "发送中..." : (codeCountdown > 0 ? `${codeCountdown}s` : "获取验证码") }}
                            </button>
                        </div>
                    </a-form-item>
                    <a-form-item label="新密码">
                        <a-input-password v-model:value="form.next" placeholder="至少 8 位，包含字母与数字" />
                        <div v-if="pwdTooShort" class="field-tip">密码不能少于6位</div>
                    </a-form-item>
                    <a-form-item label="确认新密码">
                        <a-input-password v-model:value="form.confirm" placeholder="再次输入新密码" />
                    </a-form-item>
                    <div class="form-actions">
                        <button class="back-btn" type="button" @click="handleBack">返回</button>
                        <button class="save-btn" type="button" :disabled="saving" @click="handleSave">
                            修改
                        </button>
                    </div>

                </a-form>

                <div class="strength-panel">
                    <!-- <div class="strength-header">
                        <span>密码强度</span>
                        <span class="strength-level">建议：强</span>
                    </div>
                    <div class="strength-bars">
                        <span class="bar active"></span>
                        <span class="bar active"></span>
                        <span class="bar"></span>
                    </div> -->
                    <div class="strength-tips">
                        <div class="tip-item">避免使用常见词汇或连续数字。</div>
                        <div class="tip-item">每 90 天更新一次密码更安全。</div>
                    </div>
                </div>
            </section>
        </div>


        <AppFooter active="me" />
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, ref } from "vue";
import { Modal } from "ant-design-vue";
import { useRouter } from "vue-router";
import AppFooter from "~/components/AppFooter.vue";
import { $api_UserGetEmailCheckCode, $ModifyPwdByCode } from "~/composables/user";

const router = useRouter();
const saving = ref(false);
const codeLoading = ref(false);
const codeCountdown = ref(0);
let codeTimer: ReturnType<typeof setInterval> | null = null;

const form = reactive({
    email: "",
    next: "",
    confirm: "",
    code: ""
});

const pwdTooShort = computed(() => {
    return form.next.length > 0 && form.next.length < 6;
});

const handleBack = () => {
    router.back();
};

const handleSave = () => {
    if (saving.value) return;
    if (!form.email || !form.code || !form.next || !form.confirm) {
        Modal.warning({
            title: "提示",
            content: "请填写账户",
        });
        return;
    }
    if (form.next !== form.confirm) {
        Modal.warning({
            title: "提示",
            content: "两次密码不一致",
        });
        return;
    }

    saving.value = true;
    $ModifyPwdByCode({
        method: "POST",
        body: {
            email: form.email,
            code: form.code,
            password1: form.next,
            password2: form.confirm,
        },
        server: false,
        key: "api_User_ModifyPwdByCode" + new Date().getMilliseconds(),
    })
        .then((data) => {
            const curData = data as IApiResponse<boolean>;
            if (curData?.code === 1) {
                Modal.success({
                    title: "提示",
                    content: "密码修改成功",
                    onOk: () => {
                        const link = document.createElement("a");
                        link.href = "/users/login";
                        link.rel = "noopener";
                        link.click();
                    },
                });
                return;
            }
            Modal.error({
                title: "提示",
                content: curData?.message || "修改失败",
            });
        })
        .catch((error) => {
            Modal.error({
                title: "提示",
                content: error?.message || "修改失败",
            });
        })
        .finally(() => {
            saving.value = false;
        });
};

const handleGetCode = () => {
    if (codeLoading.value || codeCountdown.value > 0) return;
    if (!form.email) {
        Modal.warning({
            title: "提示",
            content: "请填写账号",
        });
        return;
    }
    codeLoading.value = true;


    $api_UserGetEmailCheckCode({
        method: "POST",
        server: false,
        body: { email: form.email },
        key: "api_UserGetEmailCheckCode" + new Date().getMilliseconds(),
    })
        .then((data) => {
            const curData = data as IApiResponse<boolean>;
            if (curData?.code === 1) {
                Modal.success({
                    title: "提示",
                    content: curData?.message || "验证码已发送",
                });
                if (codeTimer) {
                    clearInterval(codeTimer);
                    codeTimer = null;
                }
                codeCountdown.value = 60;
                codeTimer = setInterval(() => {
                    codeCountdown.value -= 1;
                    if (codeCountdown.value <= 0 && codeTimer) {
                        clearInterval(codeTimer);
                        codeTimer = null;
                    }
                }, 1000);
                return;
            }
            Modal.error({
                title: "提示",
                content: curData?.message || "获取验证码失败",
            });
        })
        .catch((error) => {
            Modal.error({
                title: "提示",
                content: error?.message || "获取验证码失败",
            });
        })
        .finally(() => {
            codeLoading.value = false;
        });
};

onBeforeUnmount(() => {
    if (codeTimer) {
        clearInterval(codeTimer);
        codeTimer = null;
    }
});
</script>

<style scoped>
.change-password-page {
    --ink: #1d1b21;
    --muted: #6c6370;
    --accent: #ff7a59;
    --accent-strong: #ff5e3a;
    --card: #ffffff;
    --shadow: rgba(255, 94, 58, 0.18);
    --line: rgba(255, 132, 98, 0.25);
    --footer-height: 64px;

    padding: 0 16px;
    background:
        radial-gradient(circle at 8% 6%, rgba(255, 205, 176, 0.6), transparent 45%),
        radial-gradient(circle at 92% 0%, rgba(255, 222, 210, 0.7), transparent 50%),
        linear-gradient(180deg, #fff4ee 0%, #fff9f6 45%, #ffffff 100%);
    font-family: "Noto Serif SC", "Source Han Serif SC", "PingFang SC", "Microsoft YaHei", serif;
    color: var(--ink);
    position: relative;
    overflow-x: hidden;
    min-height: calc(100vh - var(--footer-height));
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
}

[v-cloak] {
    visibility: hidden;
}

.change-password-page::before,
.change-password-page::after {
    content: "";
    position: absolute;
    width: 220px;
    height: 220px;
    border-radius: 40% 60% 45% 55%;
    background: rgba(255, 160, 132, 0.18);
    filter: blur(0.5px);
    animation: float 10s ease-in-out infinite;
    z-index: 0;
}

.change-password-page::before {
    top: -80px;
    left: -40px;
}

.change-password-page::after {
    bottom: 60px;
    right: -80px;
    animation-delay: -4s;
}

.edit-header {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 12px;
    margin: 0 auto 18px;
    animation: rise 0.6s ease both;
}

.header-title {
    text-align: center;
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 0.5px;
}

.back-btn,
.save-btn {
    border: 1px solid rgba(255, 94, 58, 0.35);
    background: #fff;
    color: var(--ink);
    padding: 10px 16px;
    border-radius: 14px;
    font-size: 13px;
    font-weight: 600;
    box-shadow: 0 8px 18px rgba(255, 94, 58, 0.18);
    cursor: pointer;
    width: 100%;
}

.save-btn {
    background: var(--accent-strong);
    color: #fff;
    border-color: var(--accent-strong);
}

.save-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.card {
    position: relative;
    z-index: 1;
    margin: 0 auto 16px;
    background: var(--card);
    border-radius: 18px;
    padding: 18px;
    box-shadow: 0 18px 30px var(--shadow);
    border: 1px solid rgba(255, 210, 196, 0.55);
    display: grid;
    gap: 16px;
    animation: rise 0.7s ease both;
}

.panel {
    animation-delay: 0.05s;
}

.note-panel {
    animation-delay: 0.15s;
}

.security-banner {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 12px;
    align-items: center;
    padding: 12px;
    border-radius: 14px;
    background: linear-gradient(120deg, #fff1ea, #fff8f3);
    border: 1px dashed var(--line);
}

.badge {
    background: var(--accent);
    color: #fff;
    padding: 6px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
}

.banner-title {
    font-size: 16px;
    font-weight: 700;
}

.banner-desc {
    margin: 4px 0 0;
    font-size: 12px;
    color: var(--muted);
}

.password-form {
    display: grid;
    gap: 4px;
    width: 100%;
}

.code-row {
    display: flex;
    align-items: center;
    gap: 10px;
}

.code-btn {
    border: 1px solid rgba(255, 94, 58, 0.35);
    background: #fff;
    color: var(--ink);
    padding: 8px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    box-shadow: 0 6px 14px rgba(255, 94, 58, 0.16);
    cursor: pointer;
    white-space: nowrap;
}

.code-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.field-tip {
    margin-top: 4px;
    margin-bottom: 2px;
    font-size: 12px;
    color: #e04848;
}

.page-body {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px 0;
}

.form-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-top: 6px;
}

.strength-panel {
    display: grid;
    gap: 10px;
    padding: 12px;
    border-radius: 14px;
    background: #fff7f2;
}

.strength-header {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    font-weight: 700;
}

.strength-level {
    color: var(--accent-strong);
}

.strength-bars {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
}

.bar {
    height: 6px;
    border-radius: 999px;
    background: #f1ded7;
}

.bar.active {
    background: linear-gradient(90deg, #ff9b78, #ff6c4b);
}

.strength-tips {
    display: grid;
    gap: 6px;
    font-size: 12px;
    color: var(--muted);
}

.note-title {
    font-size: 16px;
    font-weight: 700;
}

.note-list {
    margin: 0;
    padding-left: 16px;
    color: var(--muted);
    font-size: 13px;
    display: grid;
    gap: 6px;
}

:deep(.ant-form-item) {
    margin-bottom: 12px;
    max-width: 100%;
    width: 100%;
}

:deep(.ant-form-item-label > label) {
    font-weight: 600;
    color: var(--ink);
}

:deep(.ant-input-password) {
    border-radius: 12px;
    border-color: #f1d3c8;
    padding: 8px 12px;
    width: 100%;
    box-sizing: border-box;
    max-width: 100%;
    min-height: 40px;
}

:deep(.ant-input-password input) {
    min-height: 24px;
}

:deep(.ant-input-password:hover),
:deep(.ant-input-password:focus-within) {
    border-color: var(--accent);
    box-shadow: 0 0 0 2px rgba(255, 122, 89, 0.12);
}

@keyframes rise {
    from {
        opacity: 0;
        transform: translateY(12px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes float {

    0%,
    100% {
        transform: translateY(0) rotate(-2deg);
    }

    50% {
        transform: translateY(16px) rotate(2deg);
    }
}

@media (max-width: 480px) {
    .change-password-page {
        padding-left: 12px;
        padding-right: 12px;
    }

    .edit-header {
        grid-template-columns: 1fr;
        text-align: center;
    }

    .back-btn,
    .save-btn {
        justify-self: center;
    }
}

@media (min-width: 768px) {
    .change-password-page {
        padding-top: 32px;
    }
}
</style>
